import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { analyticsApi } from '@/api';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  MetricCard,
  PageHeader,
  Pagination,
} from '@/components/ui';
import { CandidateSnapshot } from '@/features/analytics/CandidateSnapshot';
import { HiringFunnel } from '@/features/analytics/HiringFunnel';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
// Hidden for now with the sections that use them; restore together.
// import { SegmentList } from '@/features/analytics/SegmentList';
// import { TrendChart } from '@/features/analytics/TrendChart';
// import { PIPELINE_SERIES } from '@/features/analytics/series';
import { count, days, percent, rating } from '@/features/analytics/format';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

const STATUS_TONES = {
  PUBLISHED: 'success',
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  REJECTED: 'danger',
  CLOSED: 'neutral',
};

const RECOMMENDATION_LABELS = {
  STRONG_YES: 'Strong yes',
  YES: 'Yes',
  NO: 'No',
  STRONG_NO: 'Strong no',
};

export function CompanyAnalyticsPage() {
  const [period, setPeriod] = useState(30);
  const [page, setPage] = useState(1);

  const {
    data: overview,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-analytics', 'overview', period],
    queryFn: () => analyticsApi.getCompanyOverview(period),
  });

  // Not fetched while the "Over time" section is hidden.
  // const { data: trends } = useQuery({
  //   queryKey: ['company-analytics', 'trends', period],
  //   queryFn: () => analyticsApi.getCompanyTrends(period),
  // });

  const { data: outcomes } = useQuery({
    queryKey: ['company-analytics', 'outcomes', period],
    queryFn: () => analyticsApi.getOutcomes(period),
  });

  const { data: candidates } = useQuery({
    queryKey: ['company-analytics', 'candidates', period],
    queryFn: () => analyticsApi.getCandidateInsights(period),
  });

  const { data: listings } = useQuery({
    queryKey: ['company-analytics', 'listings', page],
    queryFn: () => analyticsApi.getInternshipPerformance({ page, page_size: 10 }),
  });

  if (isPending) return <LoadingState label="Crunching your numbers…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const totals = overview.totals;
  const speed = overview.speed;
  const rows = listings?.data ?? [];
  const meta = listings?.meta;

  return (
    <div>
      <PageHeader
        title="Analytics"
        description="How your hiring is going, and where candidates fall out."
        action={<PeriodPicker value={period} onChange={setPeriod} />}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Applications"
          icon="application"
          value={count(totals.applications)}
          hint={`In the last ${period} days`}
        />
        <MetricCard
          label="Interviews"
          icon="calendar"
          tone="info"
          value={count(totals.interviews)}
          hint={`${percent(outcomes?.interviews?.acceptance_rate)} invitations accepted`}
        />
        <MetricCard
          label="Offers"
          icon="document"
          tone="warning"
          value={count(totals.offers)}
          hint={`${percent(outcomes?.offers?.acceptance_rate)} accepted`}
        />
        <MetricCard
          label="Hires"
          icon="check"
          tone="success"
          value={count(totals.hires)}
          hint={`${days(speed.median_days_to_hire)} from application`}
        />
      </div>

      <div className="mb-6 grid items-start gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardHeader
            title="Hiring funnel"
            description="Applications received in this period, and how far each one got."
          />
          <HiringFunnel stages={overview.funnel} />
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Responsiveness" description="How long candidates wait on you." />
            <dl className="space-y-3 text-sm">
              <Metric
                label="First response"
                value={days(speed.median_days_to_first_response)}
                hint="Median wait for any answer at all"
              />
              <Metric
                label="Applications answered"
                value={percent(speed.response_rate)}
                hint="The rest are still sitting in the inbox"
              />
              <Metric
                label="Time to hire"
                value={days(speed.median_days_to_hire)}
                hint="Application to accepted offer"
              />
            </dl>
          </Card>

          <Card>
            <CardHeader title="Where they went" description="Everyone who left the pipeline." />
            <dl className="space-y-3 text-sm">
              <Metric label="Still in progress" value={count(overview.exits.in_progress)} />
              <Metric label="Rejected" value={count(overview.exits.rejected)} />
              <Metric
                label="Withdrew"
                value={count(overview.exits.withdrawn)}
                hint="Candidates who stepped out themselves"
              />
            </dl>
          </Card>
        </div>
      </div>

      {/* Hidden for now.
      <Card className="mb-6">
        <CardHeader
          title="Over time"
          description="Applications, interviews, offers and hires as they happened."
        />
        <TrendChart
          points={trends?.data ?? []}
          series={PIPELINE_SERIES}
          granularity={trends?.meta?.granularity}
        />
      </Card>
      */}

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Interviews"
            description="How the conversations you booked turned out."
          />
          <dl className="mb-4 space-y-3 text-sm">
            <Metric
              label="Invitations accepted"
              value={percent(outcomes?.interviews?.acceptance_rate)}
              hint={`${count(outcomes?.interviews?.declined)} declined`}
            />
            <Metric
              label="Missed interviews"
              value={percent(outcomes?.interviews?.no_show_rate)}
              hint={`${count(outcomes?.interviews?.rescheduled)} were rescheduled at least once`}
            />
            <Metric
              label="Average rating"
              value={rating(outcomes?.interviews?.average_rating)}
              hint="From your interviewers' feedback"
            />
          </dl>
          <RecommendationBar recommendations={outcomes?.interviews?.recommendations} />
        </Card>

        <Card>
          <CardHeader title="Offers" description="What happened once you made the offer." />
          <dl className="space-y-3 text-sm">
            <Metric
              label="Accepted"
              value={percent(outcomes?.offers?.acceptance_rate)}
              hint="Of the offers that got an answer"
            />
            <Metric
              label="Answer time"
              value={days(outcomes?.offers?.median_days_to_respond)}
              hint="Median wait for a yes or no"
            />
            <Metric
              label="Lapsed"
              value={percent(outcomes?.offers?.lapse_rate)}
              hint="Passed their respond-by date unanswered"
            />
          </dl>
        </Card>
      </div>

      <CandidateSnapshot insights={candidates} period={period} className="mb-6" />

      {/* Hidden for now; the brief card above replaces these three.
      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader title="Universities" description="Where your applicants study." />
          <SegmentList items={candidates?.universities ?? []} emptyLabel="No applicants yet" />
        </Card>
        <Card>
          <CardHeader
            title="Skills they have"
            description="What applicants list — not what you asked for."
          />
          <SegmentList items={candidates?.skills ?? []} emptyLabel="No skills recorded" />
        </Card>
        <Card>
          <CardHeader title="Graduating" description="When your applicants finish." />
          <SegmentList
            items={candidates?.graduation_years ?? []}
            emptyLabel="No graduation years recorded"
          />
        </Card>
      </div>
      */}

      <ListCard
        toolbarStart={
          <div className="min-w-0">
            <h2 className="text-ink text-base font-semibold tracking-tight">Listing performance</h2>
            <p className="text-ink-muted mt-0.5 text-sm">
              Lifetime figures per listing — views have no timestamps, so these are not windowed.
            </p>
          </div>
        }
        state={
          rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="internship" size={22} />}
              title="No listings yet"
              description="Publish a role and its numbers appear here."
            />
          ) : null
        }
        footer={
          meta &&
          meta.total_pages > 1 && (
            <Pagination
              className=""
              page={meta.page}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <table className="data-table w-full text-sm">
          <thead className="text-ink-muted text-left">
            <tr className="border-line border-b">
              <th scope="col" className="font-medium">
                Listing
              </th>
              <th scope="col" className="font-medium">
                Views
              </th>
              <th scope="col" className="font-medium">
                Applications
              </th>
              <th scope="col" className="font-medium">
                Apply rate
              </th>
              <th scope="col" className="font-medium">
                Shortlisted
              </th>
              <th scope="col" className="font-medium">
                Hired
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-line border-b">
                <td>
                  <Link
                    to={paths.company.internshipPerformance(row.id)}
                    className="text-ink hover:text-brand-700 font-medium transition"
                  >
                    {row.title}
                  </Link>
                  <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <Badge dot tone={STATUS_TONES[row.status] ?? 'neutral'}>
                      {row.status.replace('_', ' ')}
                    </Badge>
                    <span className="text-ink-muted">
                      {row.published_at
                        ? `Live since ${formatDate(row.published_at)}`
                        : 'Never published'}
                    </span>
                  </span>
                </td>
                <td className="text-ink-muted tabular-nums">{count(row.views)}</td>
                <td className="text-ink-muted tabular-nums">{count(row.applications)}</td>
                <td className="text-ink-muted tabular-nums">
                  {percent(row.view_to_application_rate)}
                </td>
                <td className="text-ink-muted tabular-nums">{count(row.shortlisted)}</td>
                <td className="text-ink-muted tabular-nums">{count(row.hired)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
    </div>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="min-w-0">
        <span className="text-ink">{label}</span>
        {hint && <span className="text-ink-muted block text-xs">{hint}</span>}
      </dt>
      <dd className="text-ink shrink-0 font-medium">{value}</dd>
    </div>
  );
}

/** Interviewer verdicts as one stacked bar — the balance is the point, not the counts. */
function RecommendationBar({ recommendations }) {
  const entries = Object.entries(recommendations ?? {});
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  if (total === 0) return null;

  const tones = {
    STRONG_YES: 'bg-success-600',
    YES: 'bg-success-500/50',
    NO: 'bg-warning-500/70',
    STRONG_NO: 'bg-danger-500',
  };

  return (
    <div>
      <p className="text-ink-muted mb-1.5 text-xs">Interviewer recommendations</p>
      <div className="bg-surface-muted flex h-2 w-full overflow-hidden rounded-full">
        {entries.map(([key, value]) => (
          <span
            key={key}
            className={tones[key] ?? 'bg-surface-muted'}
            style={{ width: `${(value / total) * 100}%` }}
            title={`${RECOMMENDATION_LABELS[key] ?? key}: ${value}`}
          />
        ))}
      </div>
      <p className="text-ink-muted mt-1.5 flex flex-wrap gap-x-3 text-[11px]">
        {entries
          .filter(([, value]) => value > 0)
          .map(([key, value]) => (
            <span key={key}>
              {RECOMMENDATION_LABELS[key] ?? key} {value}
            </span>
          ))}
      </p>
    </div>
  );
}
